'use client'

import clsx from 'clsx'
import { useCallback } from 'react'
import { useSearchParams } from '@/lib/next-shim'
import { useTranslations } from '@/lib/next-shim'
import { Link } from '@/lib/i18n/settings'

export type ConsultationsFilterTabValue = 'future' | 'past'
export type ConsultationsFilterTabsProps = {
  defaultTab?: ConsultationsFilterTabValue
}

const tabBase =
  'flex items-center justify-center rounded-full px-4 py-1.5 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'

export function ConsultationsFilterTabs({ defaultTab }: ConsultationsFilterTabsProps) {
  const searchParams = useSearchParams()!
  const t = useTranslations('account.tabs.ConsultationsFilterTabs')

  const selectedTab = searchParams.get('tab') ?? defaultTab

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams)
      params.set(name, value)

      return params.toString()
    },
    [searchParams]
  )

  const tabs: { value: ConsultationsFilterTabValue; label: string }[] = [
    { value: 'past', label: t('switchButton.past.label') },
    { value: 'future', label: t('switchButton.future.label') },
  ]

  return (
    <div role="tablist" className="inline-flex items-center gap-1 rounded-full bg-gray-100 p-1">
      {tabs.map(({ value, label }) => {
        const isSelected = selectedTab === value
        return (
          <Link
            key={value}
            role="tab"
            aria-selected={isSelected}
            aria-current={isSelected ? 'page' : undefined}
            href={`/account/consultations?${createQueryString('tab', value)}`}
            className={clsx(tabBase, isSelected ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-800')}
          >
            {label}
          </Link>
        )
      })}
    </div>
  )
}
